import { Info } from "lucide-react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface ITruncatedTextWithTooltipProps {
  value: string;
  className?: string;
  tooltipClassName?: string;
  testId?: string;
  /** NOTE: Defaults to `${testId}-tooltip-trigger` when testId is set. */
  tooltipTriggerTestId?: string;
  title?: string;
  wrapperClassName?: string;
  /** Render the tooltip trigger as a non-focusable span instead of a button, for use
   *  inside an interactive element (button/link/menu/option) where a nested focusable
   *  control would be invalid markup. Defaults to a focusable button. */
  nonInteractiveTrigger?: boolean;
}

export const TruncatedTextWithTooltip = memo(
  ({
    value,
    className,
    tooltipClassName,
    testId,
    tooltipTriggerTestId,
    title,
    wrapperClassName,
    nonInteractiveTrigger,
  }: ITruncatedTextWithTooltipProps): React.ReactElement => {
    const { t } = useTranslation("common");
    const textRef = useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const checkOverflow = useCallback((): void => {
      if (textRef.current) {
        setIsOverflowing(
          textRef.current.scrollWidth > textRef.current.clientWidth,
        );
      }
    }, []);

    useEffect((): (() => void) => {
      checkOverflow();
      const observer = new ResizeObserver(checkOverflow);

      if (textRef.current) {
        observer.observe(textRef.current);
      }

      return (): void => {
        observer.disconnect();
      };
    }, [checkOverflow, value]);

    const resolvedTooltipTriggerTestId =
      tooltipTriggerTestId ??
      (testId ? `${testId}-tooltip-trigger` : undefined);

    const triggerProps = {
      className: cn(
        "inline-flex shrink-0 cursor-pointer",
        !nonInteractiveTrigger && "appearance-none border-0 bg-transparent p-0",
      ),
      "aria-label": t("truncated_text.show_full_tooltip"),
      "data-testid": resolvedTooltipTriggerTestId,
    };
    const triggerIcon = (
      <Info className="h-3.5 w-3.5 text-foreground-secondary" />
    );

    return (
      <div
        className={cn(
          "flex w-full min-w-0 max-w-full items-center gap-1 overflow-hidden",
          wrapperClassName,
        )}
      >
        <span
          ref={textRef}
          className={cn("min-w-0 max-w-full flex-1 truncate", className)}
          data-testid={testId}
          title={title}
        >
          {value}
        </span>
        {isOverflowing && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                {nonInteractiveTrigger ? (
                  <span {...triggerProps}>{triggerIcon}</span>
                ) : (
                  <button type="button" {...triggerProps}>
                    {triggerIcon}
                  </button>
                )}
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className={cn("max-w-sm break-words", tooltipClassName)}
              >
                {value}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  },
);
